import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { FileSpreadsheet, Loader2, Upload } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { fmt, ddmmyyyy, isZero } from "@/lib/format";

const LABELS = { date: "Date", op_bal: "Op Bal", deposit: "Deposit", withdrawal: "Withdrawal", cashout: "Cashout", bonus: "Bonus",
  overlimit: "Overlimit", freeze_bal: "Freeze Bal", available_bal: "Available Bal", diff: "Diff", note: "Note" };

const PreviewTable = ({ rows }) => (
  <div className="ledger-scroll overflow-x-auto border border-slate-300 max-h-56">
    <table className="w-max min-w-full text-xs">
      <thead className="bg-slate-100 text-[10px] uppercase tracking-[0.1em] sticky top-0">
        <tr>{["Date", "Op Bal", "Deposit", "Withdrawal", "Cashout", "Total", "Available", "Difference", "Note"].map((h) => <th key={h} className="px-2 py-1.5 text-right first:text-left">{h}</th>)}</tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} className={`border-t border-slate-200 ${r.is_settlement ? "bg-amber-50" : ""}`}>
            <td className="px-2 py-1.5 font-mono">{ddmmyyyy(r.date)}</td>
            {["op_bal", "deposit", "withdrawal", "cashout", "total", "available_bal"].map((k) => <td key={k} className="px-2 py-1.5 text-right font-mono tabular-nums">{fmt(r[k])}</td>)}
            <td className={`px-2 py-1.5 text-right font-mono font-semibold ${isZero(r.pnl) ? "text-green-700" : "text-red-700"}`}>{fmt(r.pnl)}</td>
            <td className="px-2 py-1.5">{r.is_settlement ? "SETTLEMENT " : ""}{r.note}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const ImportDialog = ({ open, onOpenChange, bookId, sheet, onDone }) => {
  const input = useRef(null);
  const [file, setFile] = useState(null);
  const [tab, setTab] = useState("");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setFile(null); setRes(null); setTab(""); } }, [open]);

  const send = async (f, t, commit) => {
    setBusy(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post(`/books/${bookId}/import`, fd, { params: { sheet, tab: t || undefined, commit } });
      setRes(data);
      setTab(data.tab);
      if (commit) {
        toast.success(`Imported ${data.imported} entries`);
        onDone();
        onOpenChange(false);
      }
    } catch (ex) {
      setRes(null);
      toast.error(errMsg(ex));
    } finally {
      setBusy(false);
    }
  };

  const pick = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setFile(f);
    send(f, "", false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="import-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold tracking-tight">Import from Excel {sheet === "sett" && <span className="text-xs bg-amber-200 px-1.5 py-0.5 align-middle">NEW SETT</span>}</DialogTitle>
          <DialogDescription>Upload .xlsx or .csv. In Google Sheets: File → Download → Microsoft Excel. Columns are matched by their header names.</DialogDescription>
        </DialogHeader>
        <input ref={input} type="file" accept=".xlsx,.csv" className="hidden" onChange={pick} data-testid="import-file-input" />
        <button data-testid="import-choose-file-btn" onClick={() => input.current?.click()} disabled={busy}
          className="w-full h-20 border-2 border-dashed border-slate-400 hover:border-slate-900 bg-white flex items-center justify-center gap-3 transition-colors">
          {busy ? <Loader2 className="animate-spin" size={18} /> : <FileSpreadsheet size={20} />}
          <span className="font-semibold text-sm truncate">{file ? file.name : "Choose your Excel / CSV file"}</span>
        </button>
        {res && (
          <div className="space-y-3" data-testid="import-preview">
            {res.tabs.length > 1 && (
              <div className="flex flex-wrap gap-1.5 items-center">
                <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500 mr-1">Tab</span>
                {res.tabs.map((t) => (
                  <button key={t} data-testid={`import-tab-${t}`} onClick={() => send(file, t, false)} disabled={busy}
                    className={`h-8 px-3 text-xs font-semibold border ${tab === t ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-300"}`}>{t}</button>
                ))}
              </div>
            )}
            <p className="text-sm" data-testid="import-summary">
              <b>{res.count}</b> entries found{res.skipped ? `, ${res.skipped} rows skipped (no valid date)` : ""}.
              {res.mismatches > 0 && <span className="text-red-700"> {res.mismatches} with Difference ≠ 0.</span>}
            </p>
            <p className="text-xs text-slate-500">Columns matched: {res.columns.map((c) => LABELS[c]).join(", ")}</p>
            {res.set_opening && (
              <p className="text-xs bg-cyan-50 border border-cyan-200 px-3 py-2">Opening balance will be set to <b className="font-mono">{fmt(res.opening.op_bal)}</b> and freeze to <b className="font-mono">{fmt(res.opening.freeze_bal)}</b> from the first row.</p>
            )}
            {res.preview.length > 0 && <PreviewTable rows={res.preview} />}
            <button data-testid="import-confirm-btn" disabled={busy || !res.count} onClick={() => send(file, tab, true)}
              className="w-full h-12 bg-slate-900 text-white font-semibold flex items-center justify-center gap-2 hover:bg-slate-800 disabled:opacity-50">
              <Upload size={16} /> Import {res.count} entries
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
